import {
  appointmentStatuses,
  schedulingDurationIncrementMinutes,
  schedulingMaximumDurationMinutes,
  schedulingMinimumDurationMinutes,
  schedulingResourceKinds,
  type AppointmentId,
  type AppointmentStatus,
  type LocationId,
  type ModalityId,
  type SchedulingCatalogStatus,
  type SchedulingResourceId,
  type SchedulingResourceKind,
  type SchedulingRevision,
  type SchedulingTimezone,
  type ServiceId,
  type StaffSkillId,
} from "./scheduling-contract";

export const adminSchedulingViews = [
  "day",
  "week",
  "resource-lanes",
  "list",
  "kanban",
] as const;
export type AdminSchedulingView = (typeof adminSchedulingViews)[number];

export const adminSchedulingCatalogFocuses = [
  "all",
  "locations",
  "rooms",
  "stations",
  "equipment",
  "modalities",
  "services",
  "staff-skills",
] as const;
export type AdminSchedulingCatalogFocus =
  (typeof adminSchedulingCatalogFocuses)[number];

export type AdminSchedulingRoute =
  | {
      readonly kind: "workspace";
      readonly view: AdminSchedulingView;
    }
  | {
      readonly kind: "catalog";
      readonly focus: AdminSchedulingCatalogFocus;
    };

const exactSchedulingRoutes: Readonly<Record<string, AdminSchedulingRoute>> = {
  "/staff/schedule": { kind: "workspace", view: "day" },
  "/admin/calendar": { kind: "workspace", view: "day" },
  "/admin/calendar/week": { kind: "workspace", view: "week" },
  "/admin/scheduler": { kind: "workspace", view: "resource-lanes" },
  "/admin/appointments": { kind: "workspace", view: "list" },
  "/admin/appointments/kanban": { kind: "workspace", view: "kanban" },
  "/admin/resources": { kind: "catalog", focus: "all" },
  "/admin/locations": { kind: "catalog", focus: "locations" },
  "/admin/rooms": { kind: "catalog", focus: "rooms" },
  "/admin/stations": { kind: "catalog", focus: "stations" },
  "/admin/equipment": { kind: "catalog", focus: "equipment" },
  "/admin/modalities": { kind: "catalog", focus: "modalities" },
  "/admin/services": { kind: "catalog", focus: "services" },
  "/admin/staff-skills": { kind: "catalog", focus: "staff-skills" },
};

function normalizeExactPath(pathname: string): string {
  const path = pathname.split(/[?#]/, 1)[0] || "/";
  return path.length > 1 ? path.replace(/\/+$/, "") : path;
}

export function resolveAdminSchedulingRoute(
  pathname: string,
): AdminSchedulingRoute | null {
  return exactSchedulingRoutes[normalizeExactPath(pathname)] ?? null;
}

export interface AdminSchedulingLocation {
  readonly id: LocationId;
  readonly displayName: string;
  readonly publicLabel: string;
  readonly timezone: SchedulingTimezone;
  readonly status: SchedulingCatalogStatus;
  readonly updatedAt: string;
}

export interface AdminSchedulingResource {
  readonly id: SchedulingResourceId;
  readonly locationId: LocationId | null;
  readonly kind: SchedulingResourceKind;
  readonly displayLabel: string;
  readonly status: SchedulingCatalogStatus;
  readonly updatedAt: string;
}

export interface AdminSchedulingModality {
  readonly id: ModalityId;
  readonly displayName: string;
  readonly publicLabel: string;
  readonly exclusiveConflict: boolean;
  readonly status: SchedulingCatalogStatus;
  readonly updatedAt: string;
}

export interface AdminSchedulingService {
  readonly id: ServiceId;
  readonly displayName: string;
  readonly publicLabel: string;
  readonly defaultDurationMinutes: number;
  readonly setupMinutes: number;
  readonly cleanupMinutes: number;
  readonly staffMinutes: number;
  readonly travelMinutes: number;
  readonly minimumLeadMinutes: number;
  readonly maximumAdvanceDays: number;
  readonly cancellationCutoffMinutes: number;
  readonly status: SchedulingCatalogStatus;
  readonly updatedAt: string;
}

export interface AdminSchedulingStaffSkill {
  readonly id: StaffSkillId;
  readonly displayLabel: string;
  readonly status: SchedulingCatalogStatus;
  readonly updatedAt: string;
}

export interface AdminServiceEligibility {
  readonly serviceId: ServiceId;
  readonly staffSkillId: StaffSkillId;
  readonly eligible: boolean;
  readonly revision: SchedulingRevision;
}

export interface AdminServiceResourceRequirement {
  readonly serviceId: ServiceId;
  readonly resourceKind: SchedulingResourceKind;
  readonly quantity: number;
  readonly revision: SchedulingRevision;
}

export interface AdminSchedulingCatalogSnapshot {
  readonly locations: readonly AdminSchedulingLocation[];
  readonly resources: readonly AdminSchedulingResource[];
  readonly modalities: readonly AdminSchedulingModality[];
  readonly services: readonly AdminSchedulingService[];
  readonly staffSkills: readonly AdminSchedulingStaffSkill[];
  readonly serviceEligibilities: readonly AdminServiceEligibility[];
  readonly resourceRequirements: readonly AdminServiceResourceRequirement[];
}

export interface AdminSchedulingAppointment {
  readonly appointmentId: AppointmentId;
  readonly serviceId: ServiceId;
  readonly locationId: LocationId;
  readonly modalityId: ModalityId | null;
  readonly resourceIds: readonly SchedulingResourceId[];
  readonly startsAt: string;
  readonly endsAt: string;
  readonly status: AppointmentStatus;
  readonly revision: SchedulingRevision;
  readonly timezone: SchedulingTimezone;
  readonly durationMinutes: number;
  readonly updatedAt: string;
}

export interface AdminSchedulingWorkspaceSnapshot {
  readonly anchorDate: string;
  readonly timezone: SchedulingTimezone;
  readonly appointments: readonly AdminSchedulingAppointment[];
  readonly locations: readonly AdminSchedulingLocation[];
  readonly resources: readonly AdminSchedulingResource[];
  readonly modalities: readonly AdminSchedulingModality[];
  readonly services: readonly AdminSchedulingService[];
}

export interface AdminSchedulingViewGroup {
  readonly id: string;
  readonly label: string;
  readonly appointments: readonly AdminSchedulingAppointment[];
}

export type AdminSchedulingAppointmentMutationRequest =
  | {
      readonly kind: "MOVE_TIME";
      readonly appointmentId: AppointmentId;
      readonly expectedRevision: SchedulingRevision;
      readonly deltaMinutes: -15 | 15;
    }
  | {
      readonly kind: "RESIZE";
      readonly appointmentId: AppointmentId;
      readonly expectedRevision: SchedulingRevision;
      readonly deltaMinutes: -15 | 15;
    }
  | {
      readonly kind: "MOVE_RESOURCE";
      readonly appointmentId: AppointmentId;
      readonly expectedRevision: SchedulingRevision;
      readonly resourceId: SchedulingResourceId;
    }
  | {
      readonly kind: "SET_STATUS";
      readonly appointmentId: AppointmentId;
      readonly expectedRevision: SchedulingRevision;
      readonly status: AppointmentStatus;
    }
  | {
      readonly kind: "UNDO";
      readonly appointmentId: AppointmentId;
      readonly expectedRevision: SchedulingRevision;
      readonly restoreRevision: SchedulingRevision;
      readonly token: string;
    };

export interface AdminSchedulingUndoGrant {
  readonly restoreRevision: SchedulingRevision;
  readonly expiresAt: string;
  readonly token: string;
}

export type AdminSchedulingAppointmentMutationOutcome =
  | {
      readonly kind: "confirmed";
      readonly appointment: AdminSchedulingAppointment;
      readonly undo: AdminSchedulingUndoGrant | null;
    }
  | { readonly kind: "conflict" }
  | { readonly kind: "rejected" };

export type AdminSchedulingCatalogEntityKind =
  | "location"
  | "resource"
  | "modality"
  | "service"
  | "staff-skill";

export type AdminSchedulingCatalogMutationRequest =
  | {
      readonly kind: "SET_CATALOG_STATUS";
      readonly entityKind: AdminSchedulingCatalogEntityKind;
      readonly entityId: string;
      readonly expectedUpdatedAt: string;
      readonly status: SchedulingCatalogStatus;
    }
  | {
      readonly kind: "SET_SERVICE_ELIGIBILITY";
      readonly serviceId: ServiceId;
      readonly staffSkillId: StaffSkillId;
      readonly expectedRevision: SchedulingRevision;
      readonly eligible: boolean;
    }
  | {
      readonly kind: "SET_RESOURCE_REQUIREMENT";
      readonly serviceId: ServiceId;
      readonly resourceKind: SchedulingResourceKind;
      readonly expectedRevision: SchedulingRevision;
      readonly quantity: number;
    };

export type AdminSchedulingCatalogMutationOutcome =
  | {
      readonly kind: "confirmed";
      readonly catalog: AdminSchedulingCatalogSnapshot;
    }
  | { readonly kind: "conflict" }
  | { readonly kind: "rejected" };

function dateParts(
  isoTimestamp: string,
  timezone: SchedulingTimezone,
): string | null {
  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) return null;

  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(date);
    const value = Object.fromEntries(
      parts
        .filter((part) => part.type !== "literal")
        .map((part) => [part.type, part.value]),
    );
    return value.year && value.month && value.day
      ? `${value.year}-${value.month}-${value.day}`
      : null;
  } catch {
    return null;
  }
}

function addCalendarDays(localDate: string, days: number): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(localDate)) return null;
  const date = new Date(`${localDate}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return null;
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function startOfWeek(localDate: string): string | null {
  const date = new Date(`${localDate}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return null;
  const mondayOffset = (date.getUTCDay() + 6) % 7;
  return addCalendarDays(localDate, -mondayOffset);
}

export function formatSchedulingTimestamp(
  isoTimestamp: string,
  timezone: SchedulingTimezone,
): string {
  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) return "Invalid time";
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    }).format(date);
  } catch {
    return "Invalid time";
  }
}

export function appointmentDurationIsValid(durationMinutes: number): boolean {
  return (
    Number.isInteger(durationMinutes) &&
    durationMinutes >= schedulingMinimumDurationMinutes &&
    durationMinutes <= schedulingMaximumDurationMinutes &&
    durationMinutes % schedulingDurationIncrementMinutes === 0
  );
}

export function isAdminSchedulingAppointment(
  value: unknown,
): value is AdminSchedulingAppointment {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<AdminSchedulingAppointment>;
  const startsAt = new Date(item.startsAt ?? "").getTime();
  const endsAt = new Date(item.endsAt ?? "").getTime();

  return (
    typeof item.appointmentId === "string" &&
    item.appointmentId.length > 0 &&
    typeof item.serviceId === "string" &&
    typeof item.locationId === "string" &&
    (item.modalityId === null || typeof item.modalityId === "string") &&
    Array.isArray(item.resourceIds) &&
    item.resourceIds.every((id) => typeof id === "string" && id.length > 0) &&
    Number.isFinite(startsAt) &&
    Number.isFinite(endsAt) &&
    endsAt > startsAt &&
    appointmentStatuses.includes(item.status as AppointmentStatus) &&
    Number.isInteger(item.revision) &&
    Number(item.revision) >= 0 &&
    typeof item.timezone === "string" &&
    appointmentDurationIsValid(Number(item.durationMinutes)) &&
    endsAt - startsAt === Number(item.durationMinutes) * 60_000 &&
    typeof item.updatedAt === "string" &&
    Number.isFinite(new Date(item.updatedAt).getTime())
  );
}

export function isAdminSchedulingWorkspaceSnapshot(
  value: unknown,
): value is AdminSchedulingWorkspaceSnapshot {
  if (!value || typeof value !== "object") return false;
  const snapshot = value as Partial<AdminSchedulingWorkspaceSnapshot>;
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(snapshot.anchorDate ?? "") ||
    typeof snapshot.timezone !== "string" ||
    !Array.isArray(snapshot.appointments) ||
    !Array.isArray(snapshot.locations) ||
    !Array.isArray(snapshot.resources) ||
    !Array.isArray(snapshot.modalities) ||
    !Array.isArray(snapshot.services)
  ) {
    return false;
  }

  const appointmentIds = new Set<string>();
  return snapshot.appointments.every(
    (appointment) =>
      isAdminSchedulingAppointment(appointment) &&
      !appointmentIds.has(appointment.appointmentId) &&
      Boolean(appointmentIds.add(appointment.appointmentId)),
  );
}

export function isAdminSchedulingCatalogSnapshot(
  value: unknown,
): value is AdminSchedulingCatalogSnapshot {
  if (!value || typeof value !== "object") return false;
  const snapshot = value as Partial<AdminSchedulingCatalogSnapshot>;
  return (
    Array.isArray(snapshot.locations) &&
    Array.isArray(snapshot.resources) &&
    Array.isArray(snapshot.modalities) &&
    Array.isArray(snapshot.services) &&
    Array.isArray(snapshot.staffSkills) &&
    Array.isArray(snapshot.serviceEligibilities) &&
    Array.isArray(snapshot.resourceRequirements)
  );
}

function sortedAppointments(
  appointments: readonly AdminSchedulingAppointment[],
): readonly AdminSchedulingAppointment[] {
  return [...appointments].sort(
    (left, right) =>
      left.startsAt.localeCompare(right.startsAt) ||
      left.appointmentId.localeCompare(right.appointmentId),
  );
}

function resourceLabel(
  snapshot: AdminSchedulingWorkspaceSnapshot,
  resourceId: SchedulingResourceId | undefined,
): string {
  if (!resourceId) return "Unassigned";
  return (
    snapshot.resources.find((resource) => resource.id === resourceId)
      ?.displayLabel ?? "Unknown resource"
  );
}

export function buildAdminSchedulingViewGroups(
  snapshot: AdminSchedulingWorkspaceSnapshot,
  view: AdminSchedulingView,
): readonly AdminSchedulingViewGroup[] {
  if (view === "list") {
    return [
      {
        id: "all",
        label: "All appointments",
        appointments: sortedAppointments(snapshot.appointments),
      },
    ];
  }

  if (view === "kanban") {
    return appointmentStatuses.map((status) => ({
      id: status,
      label: status.replaceAll("_", " "),
      appointments: sortedAppointments(
        snapshot.appointments.filter(
          (appointment) => appointment.status === status,
        ),
      ),
    }));
  }

  if (view === "resource-lanes") {
    const groups = new Map<string, AdminSchedulingAppointment[]>();
    for (const appointment of snapshot.appointments) {
      const resourceId = appointment.resourceIds[0];
      const key = resourceId ?? "unassigned";
      const items = groups.get(key) ?? [];
      items.push(appointment);
      groups.set(key, items);
    }
    return [...groups.entries()]
      .map(([id, appointments]) => ({
        id,
        label: resourceLabel(
          snapshot,
          id === "unassigned" ? undefined : (id as SchedulingResourceId),
        ),
        appointments: sortedAppointments(appointments),
      }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }

  if (view === "day") {
    return [
      {
        id: snapshot.anchorDate,
        label: snapshot.anchorDate,
        appointments: sortedAppointments(
          snapshot.appointments.filter(
            (appointment) =>
              dateParts(appointment.startsAt, snapshot.timezone) ===
              snapshot.anchorDate,
          ),
        ),
      },
    ];
  }

  const weekStart = startOfWeek(snapshot.anchorDate);
  if (!weekStart) return [];
  return Array.from({ length: 7 }, (_, index) => {
    const localDate = addCalendarDays(weekStart, index) ?? weekStart;
    return {
      id: localDate,
      label: localDate,
      appointments: sortedAppointments(
        snapshot.appointments.filter(
          (appointment) =>
            dateParts(appointment.startsAt, snapshot.timezone) === localDate,
        ),
      ),
    };
  });
}

export function catalogResourceKindsForFocus(
  focus: AdminSchedulingCatalogFocus,
): readonly SchedulingResourceKind[] {
  if (focus === "rooms") return ["ROOM"];
  if (focus === "stations") return ["STATION"];
  if (focus === "equipment") return ["EQUIPMENT", "SHARED_FACILITY"];
  return schedulingResourceKinds;
}

export function applyConfirmedSchedulingAppointment(
  snapshot: AdminSchedulingWorkspaceSnapshot,
  current: AdminSchedulingAppointment,
  outcome: AdminSchedulingAppointmentMutationOutcome,
): AdminSchedulingWorkspaceSnapshot | null {
  if (
    outcome.kind !== "confirmed" ||
    !isAdminSchedulingAppointment(outcome.appointment) ||
    outcome.appointment.appointmentId !== current.appointmentId ||
    outcome.appointment.revision !== current.revision + 1 ||
    outcome.appointment.updatedAt <= current.updatedAt
  ) {
    return null;
  }

  const matches = snapshot.appointments.filter(
    (item) => item.appointmentId === current.appointmentId,
  );
  if (matches.length !== 1 || matches[0]?.revision !== current.revision) {
    return null;
  }

  return {
    ...snapshot,
    appointments: snapshot.appointments.map((item) =>
      item.appointmentId === current.appointmentId ? outcome.appointment : item,
    ),
  };
}

export function previewSchedulingAppointmentMutation(
  current: AdminSchedulingAppointment,
  request: AdminSchedulingAppointmentMutationRequest,
): AdminSchedulingAppointment | null {
  if (
    request.appointmentId !== current.appointmentId ||
    request.expectedRevision !== current.revision ||
    request.kind === "UNDO"
  ) {
    return null;
  }
  if (request.kind === "MOVE_TIME") {
    const delta = request.deltaMinutes * 60_000;
    return {
      ...current,
      startsAt: new Date(Date.parse(current.startsAt) + delta).toISOString(),
      endsAt: new Date(Date.parse(current.endsAt) + delta).toISOString(),
    };
  }
  if (request.kind === "RESIZE") {
    const durationMinutes = current.durationMinutes + request.deltaMinutes;
    if (!appointmentDurationIsValid(durationMinutes)) return null;
    return {
      ...current,
      durationMinutes,
      endsAt: new Date(
        Date.parse(current.endsAt) + request.deltaMinutes * 60_000,
      ).toISOString(),
    };
  }
  if (request.kind === "MOVE_RESOURCE") {
    if (current.resourceIds.includes(request.resourceId)) return null;
    return {
      ...current,
      resourceIds: [
        request.resourceId,
        ...current.resourceIds
          .slice(1)
          .filter((id) => id !== request.resourceId),
      ],
    };
  }
  if (request.status === current.status) return null;
  return { ...current, status: request.status };
}

export function applySchedulingPreview(
  snapshot: AdminSchedulingWorkspaceSnapshot,
  preview: AdminSchedulingAppointment | null,
): AdminSchedulingWorkspaceSnapshot {
  if (!preview) return snapshot;
  return {
    ...snapshot,
    appointments: snapshot.appointments.map((appointment) =>
      appointment.appointmentId === preview.appointmentId
        ? preview
        : appointment,
    ),
  };
}
